import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import type {
  ClientInquiryAnswerRecord,
  ClientInquiryAnswersPage,
  ClientInquiryKindFilter,
} from "./clientInquiries.types";
import { isSessionEnded } from "stores/sessionGuard";

/** Page sizes offered by the grid. The API caps `$limit` at 200. */
export const CLIENT_INQUIRY_PAGE_SIZES = [25, 50, 100] as const;
export const CLIENT_INQUIRY_DEFAULT_PAGE_SIZE = 50;

/**
 * What the list is showing, so the view can tell "empty" from "failed":
 *   - `unresolved`: the server returned rows but they could not be read back
 *     from the ServiceStore cache. Never shown as empty — the rows exist.
 */
export type ClientInquiryListState =
  | "idle"
  | "loading"
  | "error"
  | "unresolved"
  | "empty"
  | "rows";

export interface ClientOption {
  id: string;
  label: string;
}

/** One row of the `client-inquiry-answer-clients` facet (mirror of the API). */
export interface AnswerClientRow {
  clientId: string;
  clientName: string | null;
  clientCode: string | null;
}

interface ClientSummary {
  id: string;
  name?: string | null;
  code?: string | null;
}

const labelOf = (name?: string | null, code?: string | null): string | null => {
  const n = name?.trim();
  const c = code?.trim();
  if (n && c) return `${n} (${c})`;
  return n || c || null;
};

/**
 * View store for client-inquiry answers — used TWICE, as two registered
 * instances of this one class:
 *
 *   - `clientInquiryAnswersViewStore` — the Clients → Client Inquiries page,
 *     with a client filter and a kind filter;
 *   - `clientInquiryAnswersTabStore`  — the Client 360 "Inquiries" tab, pinned
 *     to that client with {@link initializeForClient} (its client filter is
 *     fixed and not offered).
 *
 * Rules (packages/web/CLAUDE.md): a plain class with `makeAutoObservable`, not
 * a ViewStore subclass, and NO API entities held here. The answer rows live in
 * the `client-inquiry-answers` ServiceStore's entity cache; this store keeps
 * only the ORDERED IDS of the page on screen plus the server `total`, and
 * {@link rows} resolves them against the cache. That ServiceStore is
 * merge-mode with `findConcurrency: "parallel"` (clients.stores.ts), so every
 * page either instance fetched stays resolvable — the rows are append-only,
 * so a cached row is never stale.
 *
 * Paging and filtering are SERVER-side: the table grows without bound, and the
 * API orders newest-answered first. Filters map to the API's two filters,
 * `clientId` and `kind`, plus `$limit` / `$skip`.
 *
 * A change of QUERY (client, kind, page, page size) clears the page on screen
 * synchronously, so rows from the previous query are never shown — or
 * clickable — under the new one. A same-query refresh keeps them (dimmed by
 * the view while `loading`).
 *
 * Read-only: there is no create/patch/remove anywhere on this path — the API
 * registers find/get only.
 */
class ClientInquiryAnswersStore {
  rootStore: RootStore;

  /** Ids of the current page, in the server's order (newest answered first). */
  pageIds: number[] = [];
  /** Server count for the current filters; null until a load succeeds. */
  total: number | null = null;

  loading = false;
  error: string | null = null;
  /** True once a load for the CURRENT query has finished (success or failure). */
  loaded = false;

  // Filters. `clientFilter` "" = every client.
  clientFilter = "";
  kindFilter: ClientInquiryKindFilter = "all";
  /**
   * Set by {@link initializeForClient}: the Client 360 tab's scope. While set,
   * it overrides `clientFilter` and the client column/filter are not offered.
   */
  lockedClientId: string | null = null;

  // Pagination (0-based page, as MUI's TablePagination counts).
  page = 0;
  pageSize: number = CLIENT_INQUIRY_DEFAULT_PAGE_SIZE;

  /** The answer open in the detail drawer, by id. */
  selectedId: number | null = null;

  /** Monotonic load counter — a slower, older response never lands. */
  private loadSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- Service access ------------------------------------------------------

  /** The registered `client-inquiry-answers` ServiceStore (clients.stores.ts). */
  private get answersService(): ServiceStoreType | null {
    const rs = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (name: string) => unknown };
      clientInquiryAnswersStore?: unknown;
    };
    return (
      ((rs.serviceStoreManager?.getStore?.("client-inquiry-answers") ??
        rs.clientInquiryAnswersStore) as ServiceStoreType | null | undefined) ?? null
    );
  }

  /**
   * The `client-inquiry-answer-clients` facet ServiceStore: every client that
   * has answers, archived included (clients.stores.ts).
   */
  private get facetService(): ServiceStoreType | null {
    const rs = this.rootStore as unknown as {
      serviceStoreManager?: { getStore?: (name: string) => unknown };
      clientInquiryAnswerClientsStore?: unknown;
    };
    return (
      ((rs.serviceStoreManager?.getStore?.("client-inquiry-answer-clients") ??
        rs.clientInquiryAnswerClientsStore) as ServiceStoreType | null | undefined) ?? null
    );
  }

  /** The facet rows, from the ServiceStore cache. */
  private get facetClients(): AnswerClientRow[] {
    return (this.facetService?.entities ?? []) as unknown as AnswerClientRow[];
  }

  /** The global client list (rootStore.clientStore), for the filter. */
  private get clientStore():
    | { clients?: ClientSummary[]; fetchClients?: () => Promise<void> }
    | undefined {
    return (this.rootStore as unknown as { clientStore?: never }).clientStore;
  }

  // ---- Derived -------------------------------------------------------------

  /** The client the query is scoped to, or null for every client. */
  get effectiveClientId(): string | null {
    if (this.lockedClientId) return this.lockedClientId;
    return this.clientFilter || null;
  }

  /** Whether the grid should show a Client column / client filter. */
  get showClient(): boolean {
    return !this.lockedClientId;
  }

  /** The Feathers query for the current filters and page. */
  get query(): Record<string, unknown> {
    const query: Record<string, unknown> = {
      $limit: this.pageSize,
      $skip: this.page * this.pageSize,
    };
    const clientId = this.effectiveClientId;
    if (clientId) query.clientId = clientId;
    if (this.kindFilter !== "all") query.kind = this.kindFilter;
    return query;
  }

  /** Cached answer entities (the ServiceStore owns them). */
  private get cached(): ClientInquiryAnswerRecord[] {
    return (this.answersService?.entities ?? []) as unknown as ClientInquiryAnswerRecord[];
  }

  /** The page on screen, resolved from the ServiceStore cache, in page order. */
  get rows(): ClientInquiryAnswerRecord[] {
    const cached = this.cached;
    const out: ClientInquiryAnswerRecord[] = [];
    for (const id of this.pageIds) {
      const row = cached.find((a) => a.id === id);
      if (row) out.push(row);
    }
    return out;
  }

  get selected(): ClientInquiryAnswerRecord | null {
    if (this.selectedId === null) return null;
    return this.cached.find((a) => a.id === this.selectedId) ?? null;
  }

  /**
   * The list's state. Decided from what the SERVER said (`pageIds`), never
   * from what happens to resolve: a failure is never "empty", rows the cache
   * cannot produce are "unresolved" (not "empty"), and nothing is called empty
   * while its query is still loading.
   */
  get listState(): ClientInquiryListState {
    if (this.error) return "error";
    if (this.pageIds.length === 0) {
      if (this.loading) return "loading";
      if (!this.loaded) return "idle";
      return "empty";
    }
    if (this.rows.length < this.pageIds.length) return "unresolved";
    return "rows";
  }

  /** True while a same-query refresh runs over rows already on screen. */
  get refreshing(): boolean {
    return this.loading && this.pageIds.length > 0;
  }

  /** Whether any filter narrows the list (the empty state says so). */
  get isFiltered(): boolean {
    return this.kindFilter !== "all" || (!this.lockedClientId && !!this.clientFilter);
  }

  /** Last valid 0-based page for the current total (0 when unknown/empty). */
  get lastPage(): number {
    if (!this.total || this.pageSize <= 0) return 0;
    return Math.max(0, Math.ceil(this.total / this.pageSize) - 1);
  }

  /**
   * Client filter options, in order of authority:
   *   1. the `client-inquiry-answer-clients` facet — EVERY client that has
   *      answers, archived ones included, whatever page is on screen;
   *   2. the live global client list (capped at 200, archived excluded);
   *   3. clients seen on a loaded answer (covers a facet that failed to load);
   *   4. the selected client, always, so the select never loses its value.
   * Sorted by label.
   */
  get clientOptions(): ClientOption[] {
    const byId = new Map<string, ClientOption>();
    for (const c of this.facetClients) {
      byId.set(c.clientId, { id: c.clientId, label: labelOf(c.clientName, c.clientCode) ?? c.clientId });
    }
    for (const c of this.clientStore?.clients ?? []) {
      if (byId.has(c.id)) continue;
      byId.set(c.id, { id: c.id, label: labelOf(c.name, c.code) ?? c.id });
    }
    for (const a of this.cached) {
      if (byId.has(a.clientId)) continue;
      const label = labelOf(a.clientName, a.clientCode);
      byId.set(a.clientId, { id: a.clientId, label: label ?? a.clientId });
    }
    // The selected client always stays selectable, even if nothing above lists it.
    if (this.clientFilter && !byId.has(this.clientFilter)) {
      byId.set(this.clientFilter, { id: this.clientFilter, label: this.clientLabel(this.clientFilter) });
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * "Name (code)" for a clientId. The answer's own joined name/code wins (it
   * covers archived clients); then the one global client list (never a per-row
   * fetch); then the raw id.
   */
  clientLabel(clientId: string, answer?: Pick<ClientInquiryAnswerRecord, "clientName" | "clientCode"> | null): string {
    const own = answer ? labelOf(answer.clientName, answer.clientCode) : null;
    if (own) return own;
    const fromAnswers = this.cached.find((a) => a.clientId === clientId);
    const seen = fromAnswers ? labelOf(fromAnswers.clientName, fromAnswers.clientCode) : null;
    if (seen) return seen;
    const facet = this.facetClients.find((c) => c.clientId === clientId);
    const fromFacet = facet ? labelOf(facet.clientName, facet.clientCode) : null;
    if (fromFacet) return fromFacet;
    const client = (this.clientStore?.clients ?? []).find((c) => c.id === clientId);
    return (client && labelOf(client.name, client.code)) || clientId;
  }

  // ---- Actions -------------------------------------------------------------

  /**
   * The query changed: drop the page on screen NOW (and the open answer), so
   * nothing from the previous query is visible or clickable while the new one
   * loads, then load.
   *
   * `filterChanged` (client or kind): the server `total` belonged to the OLD
   * filter, so it is cleared too — the count and the pagination stay hidden
   * until the new total arrives, instead of showing "500 answers" under a
   * client that has three. A page or page-size change keeps the same filter,
   * so its total stays true and is kept.
   */
  private queryChanged(filterChanged: boolean) {
    this.pageIds = [];
    this.selectedId = null;
    this.error = null;
    this.loaded = false;
    if (filterChanged) this.total = null;
    void this.load();
  }

  setClientFilter(clientId: string) {
    this.clientFilter = clientId;
    this.page = 0;
    this.queryChanged(true);
  }

  setKindFilter(kind: ClientInquiryKindFilter) {
    this.kindFilter = kind;
    this.page = 0;
    this.queryChanged(true);
  }

  setPage(page: number) {
    this.page = Math.max(0, page);
    this.queryChanged(false);
  }

  setPageSize(size: number) {
    this.pageSize = size > 0 ? Math.min(size, 200) : CLIENT_INQUIRY_DEFAULT_PAGE_SIZE;
    this.page = 0;
    this.queryChanged(false);
  }

  select(id: number) {
    this.selectedId = id;
  }

  closeDetail() {
    this.selectedId = null;
  }

  /** The page route's onEnter (and its header Refresh). */
  async initialize() {
    this.lockedClientId = null;
    this.seedClients();
    await Promise.all([this.load(), this.loadClientFacet()]);
  }

  /**
   * The Client 360 tab's entry: pin to one client. Moving to a different
   * client resets paging, the kind filter and the open detail.
   */
  async initializeForClient(clientId: string) {
    if (this.lockedClientId !== clientId) {
      this.lockedClientId = clientId;
      this.kindFilter = "all";
      this.page = 0;
      this.total = null;
      this.pageIds = [];
      this.selectedId = null;
      this.error = null;
      this.loaded = false;
    }
    await this.load();
  }

  /**
   * The in-page Refresh control: same query, rows kept (dimmed) meanwhile. On
   * the page (not the pinned Client 360 tab) it re-reads the client facet too,
   * so a client whose first answer just arrived becomes selectable.
   */
  async refresh() {
    if (this.lockedClientId) {
      await this.load();
      return;
    }
    await Promise.all([this.load(), this.loadClientFacet()]);
  }

  /**
   * Load the clients that have answers into the facet ServiceStore. Failure is
   * non-fatal and does not touch the list's error state: the filter falls back
   * to the live client list plus clients seen on loaded answers.
   */
  async loadClientFacet() {
    const facet = this.facetService;
    if (!facet) return;
    try {
      await facet.find({ query: {}, throwOnError: true } as never);
    } catch (err) {
      console.warn("client-inquiry answer clients: facet load failed; the filter falls back", err);
    }
  }

  /** Load the global client list once, so the filter can offer names. */
  private seedClients() {
    const cs = this.clientStore;
    if (cs && !(cs.clients && cs.clients.length)) {
      void cs.fetchClients?.();
    }
  }

  async load() {
    const seq = ++this.loadSeq;
    const service = this.answersService;
    this.loading = true;
    this.error = null;
    if (!service) {
      this.loading = false;
      this.loaded = true;
      this.error = "The client-inquiry answers service is not available in this build.";
      return;
    }
    try {
      // throwOnError per call: a refused or failed find must REJECT, never
      // resolve null — null is how the library reports failure by default, and
      // treating it as "no rows" is exactly the empty-vs-error lie this screen
      // must not tell. A null result is still treated as a failure below.
      const result = (await service.find({
        query: this.query,
        throwOnError: true,
      } as never)) as unknown as ClientInquiryAnswersPage | null;
      if (seq !== this.loadSeq) return;
      if (!result || !Array.isArray(result.data)) {
        const reason = (service as unknown as { errorOnFind?: { message?: string } | null })
          .errorOnFind?.message;
        throw new Error(reason || "The server returned no answer list.");
      }
      const data = result.data;
      const total = typeof result.total === "number" ? result.total : data.length;
      // Paged past the end (rows removed from under a filter, or a shrunken
      // total): go to the last real page instead of calling the list empty.
      const lastPage = this.pageSize > 0 ? Math.max(0, Math.ceil(total / this.pageSize) - 1) : 0;
      if (data.length === 0 && total > 0 && this.page > lastPage) {
        runInAction(() => {
          this.total = total;
          this.page = lastPage;
        });
        await this.load();
        return;
      }
      runInAction(() => {
        this.pageIds = data.map((a) => a.id);
        this.total = total;
      });
    } catch (err) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        this.pageIds = [];
        this.total = null;
        this.error =
          err instanceof Error && err.message
            ? err.message
            : "Failed to load client-inquiry answers";
      });
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
          this.loaded = true;
        });
      }
    }
  }

  /**
   * Route exit: a clean slate — data, open answer, filters and pin — and every
   * in-flight load invalidated, so nothing from this visit leaks into the next
   * (another client's rows, a stale filter, a late response).
   */
  reset() {
    this.loadSeq += 1;
    this.pageIds = [];
    this.total = null;
    this.loading = false;
    this.loaded = false;
    this.error = null;
    this.selectedId = null;
    this.clientFilter = "";
    this.kindFilter = "all";
    this.lockedClientId = null;
    this.page = 0;
    this.pageSize = CLIENT_INQUIRY_DEFAULT_PAGE_SIZE;
  }
}

export default ClientInquiryAnswersStore;
